import React, { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import { ManageUseCaseTab } from "../../components/aiDetectionSections/ManageUseCaseTab.tsx";
import { ConfigureUseCaseTab } from "../../components/aiDetectionSections/ConfigureUseCaseTab.tsx";
import MainAlertServices from "../AlertServices/mainAlertServices";
import { AlertDataProvider } from "../../contexts/AlertDataContext";
import { AdministrationSectionHeader } from "./AdministrationSectionHeader.tsx";

const TAB_QUERY = "tab";
const TAB_MODELS = "models";
const TAB_CONFIGURE_USE_CASE = "configure-use-case";
const TAB_ALERT_RULES = "alert-rules";

/**
 * Administration → AI Management: inner tabs (same shell pattern as User Management
 * → Users / Roles & permissions): AI Models, Configure Use Case (FR/ANPR), and Alert Rules.
 */
export function AdministrationAiModelsManagement() {
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo(() => {
    const v = searchParams.get(TAB_QUERY);
    if (v === TAB_ALERT_RULES) return TAB_ALERT_RULES;
    if (v === TAB_CONFIGURE_USE_CASE) return TAB_CONFIGURE_USE_CASE;
    return TAB_MODELS;
  }, [searchParams]);

  const handleTabsChange = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === TAB_MODELS) {
            next.delete(TAB_QUERY);
          } else {
            next.set(TAB_QUERY, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        minHeight: 0,
        flex: 1,
        width: "100%",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Paper
          elevation={1}
          sx={{
            minHeight: 480,
            height: "auto",
            maxHeight: "none",
            flex: 1,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AdministrationSectionHeader
            title="AI Management"
            infoTooltip="Manage AI models, configure face recognition and ANPR enrollments, and define alert rules."
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: { xs: 1, sm: 2 }, pb: 1, fontFamily: "Roboto, sans-serif" }}
          />

          <Tabs
            value={tab}
            onChange={handleTabsChange}
            sx={{
              px: { xs: 1, sm: 2 },
              borderBottom: 1,
              borderColor: "divider",
              minHeight: 42,
              "& .MuiTab-root": { textTransform: "none" },
            }}
          >
            <Tab label="AI Models" value={TAB_MODELS} />
            <Tab label="Configure Use Case" value={TAB_CONFIGURE_USE_CASE} />
            <Tab label="Alert Rules" value={TAB_ALERT_RULES} />
          </Tabs>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              p: { xs: 1, sm: 2 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            {tab === TAB_MODELS ? (
              <ManageUseCaseTab isActive administrationMode embedded />
            ) : tab === TAB_CONFIGURE_USE_CASE ? (
              <ConfigureUseCaseTab administrationMode embedded />
            ) : (
              <AlertDataProvider>
                <MainAlertServices alertRulesOnly />
              </AlertDataProvider>
            )}
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
}
